import { HeartFilled } from '@ant-design/icons';
import { Button, Card, message, Modal, Spin } from 'antd';
import Meta from 'antd/es/card/Meta';
import firebase from 'firebase/app';
import React, { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router';

import { SHOW_LOGS } from '../../features/App/App';
import { useAuth } from '../../features/Auth/AuthContext';
import { fetchUserData } from '../../features/MojaTablica/Account/Identity/ConfirmIdentity/firebaseIdentityService';
import { AppRoutes } from '../../features/Routing/routing.model';
import { firebaseFunctions, firebaseStorage, firestore } from '../../firebase/firebase';
import { FirebaseCallableFunctions, FirestoreCollection } from '../../firebase/firebase.models';
import { useGetCompanyData } from '../../firebase/hooks/getCompanyData.hook';
import { getTotalIncome } from '../../firebase/hooks/getTotalIncome.hook';
import { useGetOfferImages } from '../../hooks/getOfferImages.hook';
import { ItemListTypeNames, ItemListTypes } from '../../models/googleAnalytics.model';
import { Offer, OfferStatusState, PostSigningStatus } from '../../models/offers.model';
import { gtm } from '../../reportGTM';
import { hashValue } from '../../utils/hash.utils';
import { ToastVariant } from '../Toast/toast.model';
import { toastService } from '../Toast/toast.service';

import { DeleteFavorite } from './components/DeleteFavorite';
import { DeleteOfferModal } from './components/DeleteOfferModal';
import { DocumentsOfferModal } from './components/DocumentsOfferModal';
import { DropdownButton } from './components/DropdownButton';
import { OfferBadges } from './components/OfferBadges';
import { OfferLocationButton } from './components/OfferLocationButton';
import { OfferPrice } from './components/OfferPrice';
import { RejectProtocolModal } from './components/RejectProtocolModal';
interface Props {
  offer: Offer;
  onLocationClick?: (id: string) => void;
  state?: OfferStatusState;
  addToFavoritesButton?: boolean;
  favorites?: Offer[];
  itemListType: ItemListTypes;
  favoriteButtonDisabled?: boolean;
}
const baseHostname = window.location.hostname;
const port = baseHostname === 'localhost' ? ':3000' : '';
const httpOrHttps = port ? 'http' : 'https';

export const OfferCardHorizontal: React.FC<Props> = ({
  offer,
  onLocationClick,
  state = OfferStatusState.ACTIVE,
  addToFavoritesButton = true,
  itemListType,
  favoriteButtonDisabled = false,
}) => {
  const { isAuth, showSignInModal, currentUser } = useAuth();
  const { push } = useHistory();
  const { t } = useTranslation();
  const images = useGetOfferImages(offer.id, 0, true);
  const [heartColor, setheartColor] = useState('slate');
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDocumentsModalOpen, setIsDocumentsModalOpen] = useState(false);
  const [isRejectProtocolModalOpen, setIsRejectProtocolModalOpen] = useState(false);
  const isLandlord = hashValue(currentUser?.uid) == offer.uid;
  const isAgent =
    offer.hasAgencyAgreement === true && hashValue(currentUser?.uid) === offer.uid && !!offer.originalOwnerUid;
  const [landlordData, setLandlordData] = useState(null);
  const { companyData } = useGetCompanyData(currentUser?.uid);

  // Owner or company member can edit unsigned offers
  const canEdit = isLandlord || (companyData?.id && companyData.offers?.includes(offer.id));
  const [addedToFavorites, setAddedToFavorites] = useState(false);

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      if (!currentUser?.uid) {
        return;
      }
      try {
        const data = await fetchUserData(currentUser.uid);
        if (isMounted) {
          setLandlordData(data);
        }
      } catch (error) {
        console.error('Error fetching landlord data:', error);
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [currentUser?.uid]);

  const redirectTo = useCallback(
    (path) => {
      push(path);
    },
    [push]
  );

  const handleFirestoreUpdate = useCallback(async (ref, newData) => {
    try {
      await ref.update(newData);
    } catch (error) {
      console.error('Error updating document: ', error);
    }
  }, []);

  const onShowOffer = useCallback(async () => {
    const destination = `${AppRoutes.OFFER_ROOT}/${offer.id}`;
    redirectTo(destination);
    const itemListTypeKey = ItemListTypes[itemListType] as keyof typeof ItemListTypeNames;
    const price = typeof offer.price === 'string' ? parseFloat(offer.price) : offer.price;
    const formattedPrice = price && !isNaN(price) ? price.toFixed(2) : '0.00'; // Formatowanie na 2 miejsca po przecinku
    gtm('select_item', {
      item_list_id: itemListType,
      item_list_name: ItemListTypeNames[itemListTypeKey],
      items: [
        {
          item_id: offer.id,
          item_name: offer.name,
          price: parseFloat(formattedPrice), // Przesyłanie ceny jako liczba
          affiliation: landlordData?.company?.nazwa || '',
          item_category: offer.legalMainUse,
          location_id: offer.placeId,
        },
      ],
    });
    const totalIncome = await getTotalIncome(offer.id);

    gtm('view_item', {
      currency: 'PLN',
      value: totalIncome,
      items: [
        {
          item_id: offer.id,
          item_name: offer.name,
          price: price,
          affiliation: landlordData?.company?.nazwa || '',
          item_category: offer.legalMainUse,
          location_id: offer.placeId,
        },
      ],
    });
  }, [isAuth, offer.id, t, landlordData]);

  const onEditOffer = useCallback(() => {
    redirectTo(AppRoutes.ADD_OFFER.replace(':id', offer.id));
  }, [offer.id]);

  const onDeleteOffer = useCallback(async () => {
    try {
      const offerRef = firestore.collection(FirestoreCollection.OFFERS).doc(offer.id);
      const userRef = firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid);

      await handleFirestoreUpdate(offerRef, { 'offerStatus.state': OfferStatusState.DELETED });

      const doc = await userRef.get();
      if (!doc.exists) {
        console.warn(t('NO_DOCUMENT'));
        return;
      }

      const companyId = doc.data().companyId;
      if (companyId && companyId.length > 0) {
        await deleteOfferFromCompany(companyId);
      } else {
        await deleteOfferFromUser(userRef, doc);
      }
    } catch (error) {
      console.error('Error deleting offer:', error);
    }
  }, [offer.id, t]);

  const deleteOfferFromCompany = async (companyId) => {
    const companyRef = firestore.collection(FirestoreCollection.COMPANIES).doc(companyId);
    const companyDoc = await companyRef.get();

    if (!companyDoc.exists) {
      console.warn(t('NO_DOCUMENT')); // No such document!
      return;
    }

    const updatedOffers = (companyDoc.data().offers || []).filter((item) => item !== offer.id);
    await handleFirestoreUpdate(companyRef, { offers: updatedOffers });
  };

  const deleteOfferFromUser = async (userRef, userDoc) => {
    const updatedOffers = (userDoc.data().offers || []).filter((item) => item !== offer.id);
    await handleFirestoreUpdate(userRef, { offers: updatedOffers });
  };

  const toggleModal = () => setIsModalOpen((prev) => !prev);

  const toggleDocumentsModal = () => setIsDocumentsModalOpen((prev) => !prev);
  const toggleRejectProtocolModal = () => setIsRejectProtocolModalOpen((prev) => !prev);

  const handleRevokeAgencyAgreement = () => {
    Modal.confirm({
      title: 'Rozwiązanie pełnomocnictwa',
      content:
        'Czy na pewno chcesz rozwiązać pełnomocnictwo? Oferta zostanie przeniesiona z powrotem do właściciela lokalu.',
      okText: 'Tak, rozwiąż',
      cancelText: 'Anuluj',
      okButtonProps: { danger: true },
      onOk: async () => {
        try {
          const hide = message.loading('Rozwiązywanie umowy...', 0);
          const revokeAgreement = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.REVOKE_AGENCY_AGREEMENT);
          const result = await revokeAgreement({ offerId: offer.id });

          hide();

          if (result.data.success) {
            message.success('Umowa pełnomocnictwa została rozwiązana');
            // Refresh the page to update the offer list
            setTimeout(() => window.location.reload(), 1000);
          } else if (!result.data.originalOwnerExists) {
            Modal.confirm({
              title: 'Właściciel lokalu nie ma konta',
              content: 'Właściciel lokalu nie ma konta w systemie. Czy chcesz mimo to rozwiązać umowę i usunąć ofertę?',
              okText: 'Usuń ofertę',
              cancelText: 'Anuluj',
              okButtonProps: { danger: true },
              onOk: async () => {
                // Delete the offer
                await onDeleteOffer();
                message.success('Oferta została usunięta');
                setTimeout(() => window.location.reload(), 1000);
              },
            });
          }
        } catch (error) {
          console.error('Error revoking agency agreement:', error);
          message.error('Wystąpił błąd podczas rozwiązywania umowy');
        }
      },
    });
  };

  const handleOk = () => {
    onDeleteOffer();
    toggleModal();
  };

  const onDuplicateOffer = useCallback(async () => {
    const hide = message.loading(t('OFFER.DUPLICATING'), 0); // Duplikowanie oferty...

    const originalDoc = await firestore.collection(FirestoreCollection.OFFERS).doc(offer.id).get();

    if (!originalDoc.exists) {
      hide();
      console.error(t('OFFER.OFFER_NOT_EXIST')); // Oferta nie istnieje!
      toastService.show(t('OFFER.OFFER_NOT_EXIST'), t('OFFER.ERROR'), { variant: ToastVariant.ERROR });
      return;
    }

    const originalOfferRef = firestore.collection(FirestoreCollection.OFFERS).doc(offer.id);
    const userRef = firestore.collection(FirestoreCollection.USERS).doc(currentUser.uid);

    try {
      const doc = await originalOfferRef.get();
      if (!doc.exists) {
        console.log(t('NO_DOCUMENT')); // No such document!
        return;
      }

      const offerData = Object.fromEntries(
        Object.entries({
          ...doc.data(),
          offerStatus: {
            state: OfferStatusState.UNSIGNED,
          },
          counteroffer: {
            selected: doc.data().counteroffer.selected,
          },
        }).filter(
          ([key]) =>
            ![
              'receptionOfThePremise',
              'receptionTime',
              'transferOfThePremise',
              'bookedBy',
              'signLandlordDate',
              'signTenantDate',
              'protocolData',
              'hasAgencyAgreement',
              'originalOwnerUid',
              // Agency agreement fields - must be removed to prevent:
              // 1. Data leakage (ownerDetails contains PESEL/NIP and address)
              // 2. Inconsistent state (agency agreement data without actual agreement)
              // 3. Unauthorized use of original owner's data in duplicated offer
              'agentUid',
              'ownerDetails',
              'hasMediationAgreement',
              'pendingAgencyAgreement',
            ].includes(key)
        )
      );

      const docRef = await firestore.collection(FirestoreCollection.OFFERS).add(offerData);
      const newOfferId = docRef.id;
      console.log('newOfferId', newOfferId);

      docRef.update({ id: newOfferId });

      const originalOfferId = doc.id;
      const storageRef = firebaseStorage.ref();

      const copyImages = async (sourceFolderRef, destFolderRef) => {
        const imagesList = await sourceFolderRef.listAll();

        for (const image of imagesList.items) {
          const imageBlob = await image.getDownloadURL().then((url) => fetch(url).then((res) => res.blob()));
          const newImageRef = destFolderRef.child(image.name);
          await newImageRef.put(imageBlob);
        }
      };

      const originalImagesFolderRef = storageRef.child(`offers/${originalOfferId}/images`);
      const newImagesFolderRef = storageRef.child(`offers/${newOfferId}/images`);
      await copyImages(originalImagesFolderRef, newImagesFolderRef);

      const originalSmallImagesFolderRef = storageRef.child(`offers/${originalOfferId}/images/small`);
      const newSmallImagesFolderRef = storageRef.child(`offers/${newOfferId}/images/small`);
      await copyImages(originalSmallImagesFolderRef, newSmallImagesFolderRef);

      const userDoc = await userRef.get();
      if (!userDoc.exists) {
        console.log(t('NO_DOCUMENT')); // No such user document!
        return;
      }

      if (userDoc.data()?.companyId) {
        const addOfferToCompany = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ADD_OFFER_TO_COMPANY);
        const resp = await addOfferToCompany({ companyId: userDoc.data().companyId, offerId: newOfferId });
        if (!resp.data.success) {
          message.error(t('OFFER.DUPLICATE_ERROR'));
        }
      } else {
        const userOffers = userDoc.data().offers || [];
        await handleFirestoreUpdate(userRef, { offers: [...userOffers, newOfferId] });
      }
      hide();
      message.success(t('OFFER.DUPLICATED_SUCCESS')); // Oferta została zduplikowana.
    } catch (error) {
      hide();
      message.error(t('OFFER.DUPLICATE_ERROR')); // Błąd podczas duplikowania oferty.
      console.error('Error duplicating offer: ', error);
    }
  }, [offer.id, t]);

  const addToFavorites = firebaseFunctions.httpsCallable(FirebaseCallableFunctions.ADD_TO_FAVORITES);
  const onAddToFavorites = useCallback(() => {
    if (!isAuth) {
      showSignInModal({
        title: t('OFFER.SIGN_IN_TO_FAVORITE'), // Zaloguj się aby dodać Ofertę do ulubionych
        onSuccess: () => {
          location.reload();
        },
      });
    } else {
      if (isLoading) return;
      setIsLoading(true);
      setheartColor('slate');

      addToFavorites({ offerId: offer.id, uid: currentUser.uid })
        .then(() => {
          toastService.show(t('OFFER.FAVORITE_ADDED_DESC'), t('OFFER.FAVORITE_ADDED')); // Dodano do ulubionych
          setIsLoading(false);
        })
        .catch(() => {
          toastService.show(t('OFFER.ADD_TO_FAVORITE_ERROR'), t('OFFER.ERROR'), { variant: ToastVariant.ERROR }); // Cos poszło nie tak. Spróbuj później
          setIsLoading(false);
        })
        .finally(() => {
          setAddedToFavorites(true);
        });
    }
  }, [isLoading, currentUser, offer.id, t]);

  const [tenantFormFilled, setTenantFormFilled] = useState(false);
  const [landlordFormFilled, setLandlordFormFilled] = useState(false);

  // Function to handle deposit redirect if deposit is not confirmed
  const handleDepositRedirect = (offer, baseHostname, port, httpOrHttps) => {
    const depositUrl = `${httpOrHttps}://${baseHostname}${port}/deposit/${offer.id}`;
    window.location.href = depositUrl;
  };

  // Function to handle tenant reception form redirect
  const handleTenantReceptionRedirect = (offer, baseHostname, port, httpOrHttps, currentUser, setTenantFormFilled) => {
    if (!tenantFormFilled) {
      const receptionUrlTenant = `${httpOrHttps}://${baseHostname}${port}/contract/${offer.id}/submit/${currentUser.uid}`;
      window.location.href = receptionUrlTenant;
      setTenantFormFilled(true);
    }
  };

  // Function to handle landlord reception form redirect
  const handleLandlordReceptionRedirect = (
    offer,
    baseHostname,
    port,
    httpOrHttps,
    currentUser,
    setLandlordFormFilled
  ) => {
    if (!landlordFormFilled) {
      const receptionUrlLandlord = `${httpOrHttps}://${baseHostname}${port}/indicate-transfer-person/${offer.id}/${currentUser.uid}`;
      window.location.href = receptionUrlLandlord;
      setLandlordFormFilled(true);
    }
  };

  const redirectToProtocol = useCallback(() => {
    if (!offer?.id) {
      console.error('Offer ID is not available for redirection');
      return;
    }

    const protocolUrl = `${httpOrHttps}://${baseHostname}${port}/protocol/${offer.id}`;
    window.location.href = protocolUrl;
  }, [offer?.id]);

  // Function to handle edit or show offer
  const handleEditOrShowOffer = (onEditOffer, onShowOffer) => {
    // Allow edit for unsigned offers in Moja Tablica or if user can edit (owner or agent)
    const shouldEdit = window.location.pathname.includes(AppRoutes.MOJA_TABLICA_UNSIGNED_OFFERS) && canEdit;
    shouldEdit ? onEditOffer() : onShowOffer();
  };

  // Main onClick handler using the refactored functions
  const handleOnClick = (
    offer: Offer,
    baseHostname: string,
    port: string,
    httpOrHttps: string,
    currentUser: firebase.User,
    isLandlord: boolean,
    setTenantFormFilled: React.Dispatch<React.SetStateAction<boolean>>,
    setLandlordFormFilled: React.Dispatch<React.SetStateAction<boolean>>,
    onEditOffer: () => void,
    onShowOffer: () => void
  ) => {
    SHOW_LOGS && console.log('offer.receptionOfThePremise', offer?.receptionOfThePremise);
    SHOW_LOGS && console.log('offer.transferOfThePremise', offer?.transferOfThePremise);
    switch (true) {
      case offer.offerStatus.state === OfferStatusState.SIGNED &&
        isLandlord &&
        !('transferOfThePremise' in offer) &&
        'receptionOfThePremise' in offer:
        handleLandlordReceptionRedirect(offer, baseHostname, port, httpOrHttps, currentUser, setLandlordFormFilled);
        break;

      case offer.offerStatus.state === OfferStatusState.SIGNED && !isLandlord && !('receptionOfThePremise' in offer):
        handleTenantReceptionRedirect(offer, baseHostname, port, httpOrHttps, currentUser, setTenantFormFilled);
        break;

      case offer.offerStatus.state === OfferStatusState.SIGNED &&
        !isLandlord &&
        offer.offerStatus.postSigningStatus !== PostSigningStatus.DEPOSIT_CONFIRMED &&
        offer.offerStatus.postSigningStatus !== PostSigningStatus.PROTOCOL_ATTACHED &&
        offer.offerStatus.postSigningStatus !== PostSigningStatus.PROTOCOL_CONFIRMED:
        handleDepositRedirect(offer, baseHostname, port, httpOrHttps);
        break;

      case offer?.offerStatus?.state === OfferStatusState.SIGNED &&
        !['PROTOCOL_CONFIRMED'].includes(offer?.offerStatus?.postSigningStatus) &&
        'transferOfThePremise' in offer &&
        'receptionOfThePremise' in offer:
        redirectToProtocol();
        break;

      default:
        handleEditOrShowOffer(onEditOffer, onShowOffer);
        break;
    }
  };
  return (
    <>
      <Card
        onClick={() =>
          handleOnClick(
            offer,
            baseHostname,
            port,
            httpOrHttps,
            currentUser,
            isLandlord,
            setTenantFormFilled,
            setLandlordFormFilled,
            onEditOffer,
            onShowOffer
          )
        }
        className="m-2 max-w-xs hover:shadow-md"
        hoverable
        cover={
          <div style={{ position: 'relative', height: 222 }}>
            {images?.[0] ? (
              <img src={images?.[0]} style={{ height: 222, width: '100%', objectFit: 'cover' }} />
            ) : (
              <img
                src="/images/assets/no-image-svgrepo-com.svg"
                style={{ height: 222, width: '100%', objectFit: 'cover' }}
              />
            )}
            {offer.offerStatus.state === OfferStatusState.BOOKED && (
              <div
                style={{
                  position: 'absolute',
                  top: 0,
                  left: 0,
                  right: 0,
                  bottom: 0,
                  backgroundColor: 'rgba(0, 0, 0, 0.5)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'white',
                  fontSize: '24px',
                  fontWeight: 'bold',
                  textTransform: 'uppercase',
                  letterSpacing: '2px',
                  textShadow: '2px 2px 4px rgba(0, 0, 0, 0.8)',
                }}>
                🔒 Zarezerwowane
              </div>
            )}
          </div>
        }
        extra={
          <>
            {addToFavoritesButton && (
              <Button
                disabled={favoriteButtonDisabled || addedToFavorites}
                className="relative "
                onClick={(e) => {
                  e.stopPropagation();
                  onAddToFavorites();
                }}
                icon={
                  isLoading ? (
                    <Spin />
                  ) : favoriteButtonDisabled ? (
                    <HeartFilled className={`text-gray-400`} />
                  ) : (
                    <HeartFilled className={`text-${heartColor}-600`} />
                  )
                }
              />
            )}
            {window.location.pathname.includes(AppRoutes.MOJA_TABLICA) &&
              !window.location.pathname.includes(AppRoutes.MOJA_TABLICA_FAVORITES) && (
                <DropdownButton
                  showModal={toggleModal}
                  offer={offer}
                  state={state}
                  duplicate={onDuplicateOffer}
                  showDocuments={toggleDocumentsModal}
                  isLandlord={isLandlord}
                  onRejectProtocol={toggleRejectProtocolModal}
                  onShowOffer={onShowOffer}
                  isAgent={isAgent}
                  hasCompany={!!landlordData?.companyId}
                  onRevokeAgencyAgreement={handleRevokeAgencyAgreement}
                />
              )}
            {window.location.pathname.includes(AppRoutes.MOJA_TABLICA_FAVORITES) && (
              <DeleteFavorite offerId={offer.id} uid={currentUser.uid} />
            )}
          </>
        }
        title={
          <div className="flex items-center">
            <span>{offer.name ? offer.name : t('OFFER.NO_NAME')}</span>
            {isAgent && (
              <span className="ml-2 text-xs text-blue-600 bg-blue-100 px-2 py-1 rounded">Pełnomocnictwo</span>
            )}
          </div>
        }>
        <Meta
          description={
            <>
              <OfferPrice price={offer.price} />
              <OfferBadges offer={offer} />
              <OfferLocationButton offer={offer} onLocationClick={onLocationClick} />
            </>
          }
        />
      </Card>
      <DeleteOfferModal handleOk={handleOk} isModalOpen={isModalOpen} toggleModal={toggleModal} />
      <DocumentsOfferModal isModalOpen={isDocumentsModalOpen} toggleModal={toggleDocumentsModal} offer={offer} />
      <RejectProtocolModal
        isModalOpen={isRejectProtocolModalOpen}
        toggleModal={toggleRejectProtocolModal}
        offerId={offer.id}
        uid={currentUser?.uid}
      />
    </>
  );
};
